// Features section for browser extension

export function BrowserExtensionFeatures() {
  return (
    <section className='bg-white py-16 lg:py-24'>
      <div className='container mx-auto px-4 lg:px-8'>
        <div className='mx-auto max-w-6xl'>
          <div className='mb-20 text-center'>
            <h2 className='mb-6 text-3xl font-bold text-gray-900 lg:text-5xl'>
              Powerful Features for Every Reader
            </h2>
            <p className='mx-auto max-w-3xl text-xl text-gray-600'>
              Transform your browsing experience with intelligent
              text-to-speech, seamless document handling, and personalized audio
              controls.
            </p>
          </div>

          <div className='space-y-24'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2'>
              <div className='order-2 lg:order-1'>
                <div className='mb-6 inline-flex items-center rounded-full bg-blue-50 px-4 py-2'>
                  <div className='mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600'>
                    <svg
                      className='h-3 w-3 text-white'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                    </svg>
                  </div>
                  <span className='text-sm font-medium text-blue-700'>
                    Smart Reading
                  </span>
                </div>
                <h3 className='mb-6 text-3xl font-bold text-gray-900'>
                  Smart Text Tracking
                </h3>
                <p className='mb-8 text-lg text-gray-600'>
                  Automatically tracks and reads text on any webpage with
                  intelligent content detection and natural reading flow. Our AI
                  understands document structure for seamless navigation.
                </p>
                <ul className='space-y-3'>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Intelligent content detection
                  </li>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Natural reading flow
                  </li>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Works on any website
                  </li>
                </ul>
              </div>
              <div className='order-1 lg:order-2'>
                <div className='relative'>
                  <div className='flex h-80 items-center justify-center rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-100 p-8'>
                    <div className='text-center'>
                      <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-blue-600'>
                        <svg
                          className='h-10 w-10 text-white'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                          />
                        </svg>
                      </div>
                      <div className='font-medium text-blue-600'>
                        Reading in progress...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2'>
              <div>
                <div className='relative'>
                  <div className='flex h-80 items-center justify-center rounded-3xl bg-gradient-to-br from-green-50 to-emerald-100 p-8'>
                    <div className='text-center'>
                      <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-green-600'>
                        <svg
                          className='h-10 w-10 text-white'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            d='M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 12l2 2 4-4'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                          />
                        </svg>
                      </div>
                      <div className='font-medium text-green-600'>
                        Text selected & playing
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className='mb-6 inline-flex items-center rounded-full bg-green-50 px-4 py-2'>
                  <div className='mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-green-600'>
                    <svg
                      className='h-3 w-3 text-white'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path d='M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 12l2 2 4-4' />
                    </svg>
                  </div>
                  <span className='text-sm font-medium text-green-700'>
                    Instant Audio
                  </span>
                </div>
                <h3 className='mb-6 text-3xl font-bold text-gray-900'>
                  Highlight & Play
                </h3>
                <p className='mb-8 text-lg text-gray-600'>
                  Select any text, right-click, and instantly play audio.
                  Perfect for studying specific sections or quick reference. No
                  need to process entire pages.
                </p>
                <ul className='space-y-3'>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Right-click context menu
                  </li>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Instant audio generation
                  </li>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Perfect for studying
                  </li>
                </ul>
              </div>
            </div>

            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2'>
              <div className='order-2 lg:order-1'>
                <div className='mb-6 inline-flex items-center rounded-full bg-purple-50 px-4 py-2'>
                  <div className='mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-purple-600'>
                    <svg
                      className='h-3 w-3 text-white'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' />
                    </svg>
                  </div>
                  <span className='text-sm font-medium text-purple-700'>
                    File Processing
                  </span>
                </div>
                <h3 className='mb-6 text-3xl font-bold text-gray-900'>
                  Document Upload
                </h3>
                <p className='mb-8 text-lg text-gray-600'>
                  Upload PDFs, Word docs, and other documents directly to the
                  web app for seamless audio playback and organization. Keep
                  your library synchronized.
                </p>
                <ul className='space-y-3'>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Multiple file formats
                  </li>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Direct web app integration
                  </li>
                  <li className='flex items-center text-gray-600'>
                    <svg
                      className='mr-3 h-5 w-5 text-green-500'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        clipRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        fillRule='evenodd'
                      />
                    </svg>
                    Automatic organization
                  </li>
                </ul>
              </div>
              <div className='order-1 lg:order-2'>
                <div className='relative'>
                  <div className='flex h-80 items-center justify-center rounded-3xl bg-gradient-to-br from-purple-50 to-violet-100 p-8'>
                    <div className='text-center'>
                      <div className='mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-purple-600'>
                        <svg
                          className='h-10 w-10 text-white'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                          />
                        </svg>
                      </div>
                      <div className='font-medium text-purple-600'>
                        Uploading document...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='mt-20 grid grid-cols-1 gap-8 md:grid-cols-3'>
              <div className='group text-center'>
                <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 transition-transform duration-300 group-hover:scale-110'>
                  <svg
                    className='h-8 w-8 text-white'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                    />
                  </svg>
                </div>
                <h3 className='mb-4 text-xl font-bold text-gray-900'>
                  Email Upload
                </h3>
                <p className='text-gray-600'>
                  Send documents via email for instant processing. Perfect for
                  mobile users or quick document sharing.
                </p>
              </div>

              <div className='group text-center'>
                <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 transition-transform duration-300 group-hover:scale-110'>
                  <svg
                    className='h-8 w-8 text-white'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                    />
                  </svg>
                </div>
                <h3 className='mb-4 text-xl font-bold text-gray-900'>
                  Full Page Capture
                </h3>
                <p className='text-gray-600'>
                  Send entire webpages to the app for complete audio conversion.
                  Great for articles, blogs, and research papers.
                </p>
              </div>

              <div className='group text-center'>
                <div className='mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 transition-transform duration-300 group-hover:scale-110'>
                  <svg
                    className='h-8 w-8 text-white'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                    />
                  </svg>
                </div>
                <h3 className='mb-4 text-xl font-bold text-gray-900'>
                  Advanced Controls
                </h3>
                <p className='text-gray-600'>
                  Customize playback speed, voice selection, volume, and
                  enable/disable the extension per site with granular control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
